import { useEffect } from "react";

// useVisibilityPolling adapts an Apollo query's poll cadence to tab visibility:
// it polls every foregroundMs while the tab is visible and backs off to
// backgroundMs when the tab is hidden (backgrounded), cutting needless requests
// when no one is looking. Pass the `startPolling` function returned by useQuery.
// A backgroundMs of 0 stops polling while hidden (Apollo treats 0 as "off").
//
// startPolling only restarts the clock, so a tab coming back after a long time
// hidden would keep showing what it last loaded for up to another foregroundMs.
// Pass the query's `refetch` as refetchOnVisible to load fresh data the moment
// the tab becomes visible again.
export function useVisibilityPolling(
  startPolling,
  foregroundMs,
  backgroundMs,
  refetchOnVisible,
) {
  useEffect(() => {
    if (!startPolling) return undefined;

    const applyInterval = () =>
      startPolling(document.hidden ? backgroundMs : foregroundMs);

    const onVisibilityChange = () => {
      applyInterval();
      if (!document.hidden && refetchOnVisible) {
        Promise.resolve(refetchOnVisible()).catch(() => {});
      }
    };

    applyInterval();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [startPolling, foregroundMs, backgroundMs, refetchOnVisible]);
}
